/**
 * Окружение сборки: продакшен или дев-стенд.
 *
 * Нужно, чтобы недоделанные разделы про магазины (подписки, темы, роль
 * продавца) были доступны на деве и не мозолили глаза операторам в проде.
 *
 * Два источника, в порядке убывания приоритета:
 *  1. REACT_APP_ENV из ENV_FILE ветки — общий CI кладёт его в корень контекста
 *     сборки как `.env`, а CRA запекает в бандл. Рабочий путь: этим стенды и
 *     переключаются;
 *  2. адрес, с которого открыли админку, — запасной, чтобы локальный запуск и
 *     стенд без переменной не выглядели продом.
 *
 * У первого источника есть грабля, на которую уже наступили: dotenv не трогает
 * переменные, которые в окружении сборки заданы, а пустой `ARG REACT_APP_ENV=`
 * в Dockerfile — это заданная переменная. Из-за него дев-стенд с
 * REACT_APP_ENV=development собирался как прод, пока соседние REACT_APP_* из
 * того же файла работали. Подробности — в Dockerfile, там же запрет заводить
 * ARG/ENV для этих переменных.
 *
 * Направление по умолчанию выбрано в сторону прода: неизвестный адрес и
 * пустая переменная дают продакшен. Ошибка в эту сторону означает «оператор
 * не увидел сырой раздел», в обратную — «сырой раздел уехал клиентам».
 */

export type AppEnvironment = 'production' | 'development'

/** Дев-стенды живут здесь; прод — на других доменах. */
const DEV_HOST_SUFFIX = '.dev.temkispb.ru'
const LOCAL_HOSTS = ['localhost', '127.0.0.1', '0.0.0.0']

function fromVariable(): AppEnvironment | null {
  const raw = process.env.REACT_APP_ENV?.trim().toLowerCase()
  if (!raw) return null
  // Всё, что не «дев», считаем продом: опечатка в переменной не должна
  // открывать сырые разделы.
  return raw === 'development' || raw === 'dev' ? 'development' : 'production'
}

function fromHostname(): AppEnvironment {
  // SSR/тесты без window — считаем продом.
  if (typeof window === 'undefined') return 'production'

  const host = window.location.hostname.toLowerCase()
  if (LOCAL_HOSTS.includes(host)) return 'development'
  if (host.endsWith(DEV_HOST_SUFFIX)) return 'development'
  return 'production'
}

export function resolveEnvironment(): AppEnvironment {
  return fromVariable() ?? fromHostname()
}

export const APP_ENV: AppEnvironment = resolveEnvironment()

/**
 * Разбор флага-выключателя из переменной сборки.
 *
 * Возвращает `null`, когда переменная не задана: только так вызывающий код
 * отличает «сказали скрыть» от «не сказали ничего» и может уйти на фолбэк.
 *
 * Непустое, но непонятное значение считается выключением — по той же
 * причине, что и в `fromVariable`: опечатка не должна открывать сырой
 * раздел клиентам.
 */
function readFlag(raw: string | undefined): boolean | null {
  const value = raw?.trim().toLowerCase()
  if (!value) return null
  return value === 'true' || value === '1' || value === 'yes' || value === 'on'
}

/**
 * Показывать ли разделы, относящиеся к магазинам.
 *
 * Два уровня, в порядке убывания приоритета:
 *  1. REACT_APP_SHOW_SHOP_SECTIONS — точечный выключатель именно этих
 *     разделов, если понадобится открыть их отдельно от всего остального
 *     (например, показать в проде раньше Страты);
 *  2. окружение сборки — дев показывает, прод скрывает.
 *
 * Отдельным именем, а не проверкой `APP_ENV === 'development'` по коду:
 * когда разделы доделают и откроют в проде, менять придётся одну строку,
 * а не искать все условия.
 */
export const SHOW_SHOP_SECTIONS: boolean =
  readFlag(process.env.REACT_APP_SHOW_SHOP_SECTIONS) ??
  APP_ENV === 'development'

/**
 * Показывать ли раздел Telegram целиком.
 *
 * Внутри него живут три страницы, и все три — про то, чего в проде пока нет:
 * группы категорий и топики магазинов обслуживают замороженную витрину, а
 * новости платформы публикуются в группу, которую в проде никто не привязывал.
 * Оператору это меню — тупик, поэтому прячется целиком, а не по одной странице.
 *
 * Флаг отдельный, а не общий с магазинным: новости к магазинам отношения не
 * имеют, и когда их решат включить в проде, поднять нужно будет только этот
 * выключатель, не открывая заодно витрину.
 */
export const SHOW_TELEGRAM_SECTIONS: boolean =
  readFlag(process.env.REACT_APP_SHOW_TELEGRAM_SECTIONS) ??
  APP_ENV === 'development'

/**
 * Показывать ли разделы Stratapay.
 *
 * Переезд на Страту заморожен, но код уже уехал в мастер — вырезать его из
 * мержа было нечем, слишком много всего в него вплетено. На бэке и на витрине
 * переезд гасится своими рубильниками (`STRATAPAY_ENABLED` и
 * `VITE_STRATAPAY_ENABLED`), и админка — третья сторона того же выключателя:
 * без него оператор в проде видит пункт меню, за которым пустые экраны.
 *
 * Правило то же, что у магазинных разделов: сначала точечный
 * REACT_APP_SHOW_STRATAPAY_SECTIONS, иначе окружение сборки. И ошибка по
 * умолчанию — в ту же сторону: неизвестный адрес считается продом, то есть
 * раздел скрыт.
 */
export const SHOW_STRATAPAY_SECTIONS: boolean =
  readFlag(process.env.REACT_APP_SHOW_STRATAPAY_SECTIONS) ??
  APP_ENV === 'development'
